import React from 'react';
import { ChangeEvent, useState } from 'react';
import { ValidationResponse } from './ValidationResponse';

export interface props {
  title: string;
  placeholder?: string;
  onBlur?: (value: string) => ValidationResponse | void;
  mandatory?: boolean;
  className?: string;
  initialValue?: string;
  onClick?: () => void;
  readonly?: boolean;
  hideErrorControl?: boolean;
}

enum validStates {
  uninitialised,
  invalid,
  valid
}

// This component does not handle model data as it is also composed within Editable label as well.

export const TextInput = (props: props) => {
  const [valid, setValid] = useState(props.mandatory == true ? validStates.uninitialised : validStates.valid);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = React.createRef<HTMLInputElement>();

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === props.initialValue || !props.onBlur) return;
    const validationResponse = props.onBlur(e.target.value);
    if (validationResponse) {
      if (validationResponse.isValid) {
        setValid(validStates.valid);
        setErrorMessage('');
      } else {
        setValid(validStates.invalid);
        if (validationResponse.errorMessage != undefined) {
          setErrorMessage(validationResponse.errorMessage);
        } else {
          setErrorMessage('An unknown error occurred');
        }
      }
    }
  };

  const onClick = () => {
    setFocus();
    if (props.onClick != undefined) {
      props.onClick();
    }
  };

  const setFocus = () => {
    inputRef?.current?.focus();
  };

  const fieldIsInvalid = () => valid == validStates.invalid;

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (fieldIsInvalid()) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (fieldIsInvalid()) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (fieldIsInvalid()) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  const errorExists = () => errorMessage != undefined && errorMessage != '';

  const errorControl = () =>
    errorExists() ? <label className="text-feedback-red text-xs">{errorMessage}</label> : <br />;

  return (
    <div title={props.initialValue} className={`w-full ${props.className}`}>
      <div
        className={`cursor-text ${backgroundColour()} ${borderColour()} pl-3 border-b-2 pt-2 pb-2 text-solid-white placeholder-shown:text-secondary-deep-blue-30`}
        onClick={onClick}
      >
        <label className={`cursor-text text-xs ${labelTextColour()} block`}>
          {props.mandatory == true ? '*' : ''}
          {props.title}
        </label>
        <input
          readOnly={props.readonly}
          ref={inputRef}
          className={`${backgroundColour()} focus:outline-none w-full`}
          type="text"
          placeholder={props.placeholder}
          onChange={onChange}
          value={props.initialValue}
        />
      </div>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
